import { Method } from 'consts';
import { IResConfigs } from 'types/api';

export const isDefaultSSOSettings = (configsData: IResConfigs): boolean =>
	!configsData.currentSSOId;

// True when this connection verifies identity only, creating no user. Any protocol row answers for
// it - the backend writes all three together.
//
// The OIDC value lives on the per-tenant providerSettings entry, not on the oauth object: the wrapper
// carries the rows, the entry carries the connection's settings.
export const isAuthenticationOnlyConfig = (
	configsData: IResConfigs | undefined
): boolean => {
	const tenantId = configsData?.tenant?.id;
	const provider = tenantId
		? configsData?.oauth?.providerSettings?.[tenantId]
		: undefined;

	return !!(
		configsData?.saml?.authenticationOnly ||
		provider?.authenticationOnly ||
		configsData?.xaa?.authenticationOnly
	);
};

export const getCustomSettingId = (
	configsData: IResConfigs,
	method?: Method
): string | null => {
	if (isDefaultSSOSettings(configsData)) {
		return null;
	}

	if (method === Method.Saml) {
		return configsData.tenant.SSOWrapperMap![configsData.currentSSOId]!.samlId;
	}
	if (method === Method.Oidc) {
		return configsData.tenant.SSOWrapperMap![configsData.currentSSOId]!.oauthId;
	}

	const authTypeMethod =
		configsData.tenant.SSOWrapperMap?.[configsData.currentSSOId].authType;

	const customSettingId =
		authTypeMethod === Method.Saml
			? configsData.tenant.SSOWrapperMap![configsData.currentSSOId]!.samlId
			: configsData.tenant.SSOWrapperMap![configsData.currentSSOId]!.oauthId;

	return customSettingId;
};

export const getSSODomains = (
	configsData: IResConfigs,
	method?: Method
): string[] | undefined => {
	if (isDefaultSSOSettings(configsData)) {
		return configsData.tenant.domains;
	}

	const customSettingId = getCustomSettingId(configsData, method);
	/* istanbul ignore if: no need to test this  */
	if (!configsData.tenant.domainsToSSO || !customSettingId) {
		return undefined;
	}

	return Object.entries(configsData.tenant.domainsToSSO)
		.filter(([, settingId]) => settingId === customSettingId)
		.map(([domain]) => domain);
};
